import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Copy, CopyPlus, Star, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { EstadoVacio } from '@/components/Card';
import { leerSettings } from '@/db/schema';
import { alternarFavorito, alternarFavoritoBase, leerAlimento } from '@/db/alimentos';
import { borrarComida, duplicarComida, leerComida } from '@/db/comidas';
import { alternarFavoritoPlatillo, leerPlatillo } from '@/db/platillos';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import type { Dish, MealEntry, Porcion, TiempoComida } from '@/db/types';
import { ahoraIso } from '@/domain/dates';
import { fechaDelDia } from '@/domain/hoy';
import { horaActual } from '@/domain/time';
import { formatearNumero } from '@/domain/numbers';
import {
  GRAMOS,
  NOMBRE_TIEMPO,
  calculableDeBase,
  calculableDePropio,
  calculableDeRegistro,
  calidadDe,
  estadoDe,
  formatearGramos,
  opcionesDePorcion,
  repetirRegistro,
  tiempoSegunHora,
  type Calculable,
  type OrigenActual,
  type Repeticion,
} from '@/domain/nutricion';
import {
  RACION,
  calculableDePlatillo,
  calcularPlatillo,
  pesoFinalDe,
  type ResolverIngrediente,
} from '@/domain/platillos';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { useAlimentosBase } from '@/lib/useAlimentosBase';
import { EtiquetaCalidad, EtiquetaEstado, esTiempoComida, fechaDeParametro, rutaDelDia } from './comunes';
import { FormularioRapido, FormularioRegistro, type Fuente, type InicialRegistro } from './FormularioRegistro';
import { textoRaciones, useResolverIngredientes } from './ingredientes';

/** Lo que trae la URL al abrir un formulario nuevo: fecha, tiempo y la hora actual. */
function useNuevoRegistro() {
  const hoy = useFechaHoy();
  const [parametros] = useSearchParams();
  const [abierto] = useState(() => ({ id: nuevoId('com'), hora: horaActual(), creadoEn: ahoraIso() }));
  const fecha = fechaDeParametro(parametros.get('fecha'), hoy);
  const tiempoParametro = parametros.get('tiempo');
  const tiempo: TiempoComida = esTiempoComida(tiempoParametro) ? tiempoParametro : tiempoSegunHora(abierto.hora);
  return { hoy, parametros, abierto, fecha, tiempo };
}

function NoEncontrado({ texto, volver }: { texto: string; volver: string }) {
  return (
    <div>
      <PageHeader titulo="No está." volver={{ a: volver, texto: 'Volver' }} />
      <EstadoVacio titulo={texto} descripcion="Puede que se haya borrado. Lo ya registrado no cambia." />
    </div>
  );
}

function BotonFavorito({ activo, onAlternar }: { activo: boolean; onAlternar: () => Promise<void> }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <button
        type="button"
        aria-pressed={activo}
        onClick={() => {
          setError(null);
          onAlternar().catch((e: unknown) => setError(mensajeDeError(e)));
        }}
        className="inline-flex min-h-11 items-center gap-1.5 rounded-xl bg-marca-suave px-3 text-[13px] font-medium text-texto"
      >
        <Star className={`size-4 text-marca ${activo ? 'fill-current' : ''}`} />
        {activo ? 'En favoritos' : 'Agregar a favoritos'}
      </button>
      {error !== null && <p className="text-[12px] text-alerta">{error}</p>}
    </>
  );
}

// ---------------------------------------------------------------------------
// Registrar un alimento de la base, de Mis alimentos o repetir un registro
// ---------------------------------------------------------------------------

/**
 * Un platillo con sus ingredientes de hoy, listo para repetirlo; null si ya
 * no existe o si algún ingrediente no se puede calcular.
 */
function origenDePlatillo(platillo: Dish | null | undefined, resolver: ResolverIngrediente | null): OrigenActual {
  if (platillo === null || platillo === undefined || resolver === null) return null;
  const calculo = calcularPlatillo(platillo, resolver);
  const calculable = calculableDePlatillo(platillo, calculo);
  return calculable === null ? null : { tipo: 'platillo', platillo, calculable, rompeAyuno: calculo.rompeAyuno };
}

/** "Mis platillos · 2 raciones · cocinado pesa 220 g" */
function descripcionDePlatillo(platillo: Dish): string {
  const peso = pesoFinalDe(platillo);
  return [
    'Mis platillos',
    textoRaciones(platillo.raciones),
    peso === null ? 'sin peso final, por ración' : `cocinado pesa ${formatearGramos(peso)}`,
    platillo.archivado ? 'archivado' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Al editar un registro de platillo: la ración del platillo de hoy, si tiene peso final. */
function porcionesDePlatillo(platillo: Dish | null): Porcion[] {
  const peso = platillo === null ? null : pesoFinalDe(platillo);
  return platillo === null || peso === null ? [] : [{ nombre: RACION, gramos: peso / platillo.raciones }];
}

function PlatilloSinCalcular({ platillo, errores, volver }: { platillo: Dish; errores: readonly string[]; volver: string }) {
  return (
    <div>
      <PageHeader
        titulo={platillo.nombre}
        descripcion={descripcionDePlatillo(platillo)}
        volver={{ a: volver, texto: 'Buscar', etiqueta: 'Volver a buscar' }}
      />
      <Banner tono="aviso" titulo="Este platillo no se puede registrar todavía">
        <ul className="grid gap-1">
          {errores.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      </Banner>
      <Link
        to={`/comida/platillos/${platillo.id}`}
        className="mt-4 inline-flex min-h-11 items-center text-[13px] font-medium text-marca"
      >
        Revisar el platillo
      </Link>
    </div>
  );
}

export function RegistrarPage() {
  const { origen = '', id = '' } = useParams();
  const { hoy, parametros, abierto, fecha, tiempo } = useNuevoRegistro();
  const navegar = useNavigate();
  const { base, resolver } = useResolverIngredientes();
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const propio = useLiveQuery(
    async () => (origen === 'propio' ? ((await leerAlimento(id)) ?? null) : null),
    [origen, id],
  );
  const platillo = useLiveQuery(
    async () => (origen === 'platillo' ? ((await leerPlatillo(id)) ?? null) : null),
    [origen, id],
  );
  const previo = useLiveQuery(
    async () => (origen === 'registro' ? ((await leerComida(id)) ?? null) : null),
    [origen, id],
  );
  const propioDelPrevio = useLiveQuery(
    async () =>
      previo !== undefined && previo !== null && previo.origen.tipo === 'propio'
        ? ((await leerAlimento(previo.origen.refId)) ?? null)
        : null,
    [previo],
  );
  const platilloDelPrevio = useLiveQuery(
    async () =>
      previo !== undefined && previo !== null && previo.origen.tipo === 'platillo'
        ? ((await leerPlatillo(previo.origen.refId)) ?? null)
        : null,
    [previo],
  );

  const buscar = `/comida/agregar?fecha=${fecha}&tiempo=${tiempo}`;
  if (
    base.estado === 'cargando' ||
    ajustes === undefined ||
    propio === undefined ||
    platillo === undefined ||
    previo === undefined
  ) {
    return null;
  }
  // Los platillos esperan a Mis alimentos para calcular sus ingredientes
  const conPlatillo = origen === 'platillo' || previo?.origen.tipo === 'platillo';
  if (conPlatillo && resolver === null && base.estado !== 'error') return null;

  let fuente: Fuente | null = null;
  let repeticion: Repeticion | null = null;
  let favorito: { activo: boolean; alternar: () => Promise<void> } | null = null;
  let descripcion = '';
  let fuenteUsda: string | null = null;
  /** La nota de un alimento propio: fuente, micronutrientes o cómo se calculó (0.3.5). */
  let notaDelAlimento: string | null = null;

  if (origen === 'base') {
    const alimento = base.estado === 'lista' ? base.modulo.alimentoBase(id) : undefined;
    if (alimento === undefined) return <NoEncontrado texto="Ese alimento no está en la base." volver={buscar} />;
    fuente = {
      nombre: alimento.nombre,
      origen: { tipo: 'base', refId: alimento.id },
      calculable: calculableDeBase(alimento),
      rompeAyuno: alimento.rompeAyuno,
      estado: alimento.estado,
      esBase: true,
    };
    favorito = {
      activo: (ajustes.favoritosBase ?? []).includes(alimento.id),
      alternar: () => alternarFavoritoBase(alimento.id),
    };
    descripcion = `${alimento.categoria} · ${formatearNumero(alimento.por100g.kcal, { decimales: 0 })} kcal por 100 g`;
    fuenteUsda = `Fuente: USDA FoodData Central, ${alimento.fuente.tipo} (fdcId ${alimento.fuente.fdcId}, “${alimento.fuente.descripcion}”). Valores aproximados.`;
  } else if (origen === 'propio') {
    if (propio === null) return <NoEncontrado texto="Ese alimento ya no está en Mis alimentos." volver={buscar} />;
    fuente = {
      nombre: propio.nombre,
      origen: { tipo: 'propio', refId: propio.id },
      calculable: calculableDePropio(propio),
      rompeAyuno: propio.rompeAyuno,
      estado: estadoDe(propio),
      esBase: false,
      calidad: calidadDe(propio),
    };
    favorito = { activo: propio.favorito, alternar: () => alternarFavorito(propio.id) };
    descripcion = propio.archivado ? 'Mis alimentos · archivado' : 'Mis alimentos';
    notaDelAlimento = typeof propio.nota === 'string' && propio.nota.trim() !== '' ? propio.nota : null;
  } else if (origen === 'platillo') {
    if (platillo === null) return <NoEncontrado texto="Ese platillo ya no está en Mis platillos." volver={buscar} />;
    // Con los ingredientes como están hoy (Req. 20.4)
    const calculo = resolver === null ? null : calcularPlatillo(platillo, resolver);
    const calculable: Calculable | null = calculo === null ? null : calculableDePlatillo(platillo, calculo);
    if (calculo === null || calculable === null) {
      const errores =
        calculo === null
          ? ['No se pudo abrir la base de alimentos: cierra y vuelve a abrir la app.']
          : calculo.errores.length > 0
            ? calculo.errores
            : ['Revisa sus ingredientes y sus raciones.'];
      return <PlatilloSinCalcular platillo={platillo} errores={errores} volver={buscar} />;
    }
    fuente = {
      nombre: platillo.nombre,
      origen: { tipo: 'platillo', refId: platillo.id },
      calculable,
      rompeAyuno: calculo.rompeAyuno,
      estado: null,
      esBase: false,
    };
    favorito = { activo: platillo.favorito, alternar: () => alternarFavoritoPlatillo(platillo.id) };
    descripcion = descripcionDePlatillo(platillo);
  } else if (origen === 'registro') {
    if (previo === null || previo.origen.tipo === 'rapido') {
      return <NoEncontrado texto="Ese registro ya no existe." volver={buscar} />;
    }
    if (previo.origen.tipo === 'propio' && propioDelPrevio === undefined) return null;
    if (previo.origen.tipo === 'platillo' && platilloDelPrevio === undefined) return null;
    // Con el alimento o el platillo como está hoy (por ejemplo, con su
    // etiqueta corregida); la copia del registro anterior solo si ya no existe
    // o si el platillo no se puede calcular.
    const delOrigen = previo.origen;
    const alimentoDeBase =
      delOrigen.tipo === 'base' && base.estado === 'lista' ? base.modulo.alimentoBase(delOrigen.refId) : undefined;
    const actual: OrigenActual =
      alimentoDeBase !== undefined
        ? { tipo: 'base', alimento: alimentoDeBase }
        : delOrigen.tipo === 'propio' && propioDelPrevio !== null && propioDelPrevio !== undefined
          ? { tipo: 'propio', alimento: propioDelPrevio }
          : delOrigen.tipo === 'platillo'
            ? origenDePlatillo(platilloDelPrevio, resolver)
            : null;
    repeticion = repetirRegistro(previo, actual);
    if (repeticion === null) return <NoEncontrado texto="Ese registro no se puede repetir." volver={buscar} />;
    fuente = {
      nombre: repeticion.nombre,
      origen: repeticion.origen,
      calculable: repeticion.calculable,
      rompeAyuno: repeticion.rompeAyuno,
      estado: repeticion.estado,
      esBase: repeticion.actual && repeticion.origen.tipo === 'base',
      ...(repeticion.actual ? {} : { nota: 'Valores del registro anterior.' }),
      // La del alimento como está hoy; si ya no existe, la del registro anterior
      calidad:
        repeticion.actual && repeticion.origen.tipo === 'propio' && propioDelPrevio !== null && propioDelPrevio !== undefined
          ? calidadDe(propioDelPrevio)
          : repeticion.actual
            ? null
            : calidadDe(previo),
    };
    const que = delOrigen.tipo === 'platillo' ? 'platillo' : 'alimento';
    descripcion = repeticion.actual
      ? `Con los valores actuales del ${que}. Lo que ya registraste no cambia.`
      : delOrigen.tipo === 'platillo' && platilloDelPrevio !== null
        ? 'Con los valores de la vez anterior: el platillo no se puede calcular ahora. Revisa sus ingredientes.'
        : `Con los valores de la vez anterior: el ${que} ya no existe.`;
  } else {
    return <NoEncontrado texto="Ese alimento no existe." volver={buscar} />;
  }

  const opciones = opcionesDePorcion(fuente.calculable);
  const porcionParametro = parametros.get('porcion');
  const porcion =
    repeticion !== null
      ? repeticion.porcion
      : porcionParametro !== null && opciones.some((o) => o.clave === porcionParametro)
        ? porcionParametro
        : (opciones[0]?.clave ?? GRAMOS);
  const cantidad =
    repeticion !== null
      ? String(repeticion.cantidad)
      : (parametros.get('cantidad') ?? (porcion === GRAMOS ? '100' : '1'));

  const inicial: InicialRegistro = {
    id: abierto.id,
    fecha,
    hora: abierto.hora,
    tiempo,
    cantidad,
    porcion,
    nota: '',
    creadoEn: abierto.creadoEn,
  };

  return (
    <div>
      <PageHeader
        antetitulo={`${NOMBRE_TIEMPO[tiempo]} · ${fechaDelDia(fecha)}`}
        titulo={fuente.nombre}
        descripcion={descripcion}
        volver={{ a: buscar, texto: 'Buscar', etiqueta: 'Volver a buscar' }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <EtiquetaEstado estado={fuente.estado} />
          <EtiquetaCalidad calidad={fuente.calidad ?? null} />
          {!fuente.rompeAyuno && <span className="text-[12px] text-texto-suave">No rompe el ayuno</span>}
          {favorito !== null && <BotonFavorito activo={favorito.activo} onAlternar={favorito.alternar} />}
        </div>
      </PageHeader>

      <FormularioRegistro
        fuente={fuente}
        inicial={inicial}
        textoGuardar="Guardar"
        onGuardado={(r) => void navegar(rutaDelDia(r.fecha, hoy, r.tiempo), { replace: true })}
      />

      {notaDelAlimento !== null && (
        <p className="mt-6 text-[12px] leading-relaxed text-texto-suave">{notaDelAlimento}</p>
      )}

      {origen === 'base' && (
        <div className="mt-6 space-y-2 text-[12px] leading-relaxed text-texto-suave">
          <p>{fuenteUsda}</p>
          <Link
            to={`/comida/mis-alimentos/nuevo?desdeBase=${id}&volver=${encodeURIComponent(buscar)}`}
            className="inline-flex min-h-11 items-center text-[13px] font-medium text-marca"
          >
            Copiar a Mis alimentos para ajustarlo
          </Link>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registro rápido nuevo
// ---------------------------------------------------------------------------

export function RapidoPage() {
  const { hoy, parametros, abierto, fecha, tiempo } = useNuevoRegistro();
  const navegar = useNavigate();
  const desde = parametros.get('desde');
  const previo = useLiveQuery(async () => (desde === null ? null : ((await leerComida(desde)) ?? null)), [desde]);
  if (previo === undefined) return null;

  const texto = (v: number | null) => (v === null ? '' : formatearNumero(v, { decimales: 1, fijos: false }).replace(/,/g, ''));
  const buscar = `/comida/agregar?fecha=${fecha}&tiempo=${tiempo}`;

  return (
    <div>
      <PageHeader
        antetitulo={`${NOMBRE_TIEMPO[tiempo]} · ${fechaDelDia(fecha)}`}
        titulo="Registro rápido."
        descripcion="Cuando no está en la base: nombre y kcal, y los macros si los sabes."
        volver={{ a: buscar, texto: 'Buscar', etiqueta: 'Volver a buscar' }}
      />
      <FormularioRapido
        inicial={{
          id: abierto.id,
          fecha,
          hora: abierto.hora,
          tiempo,
          cantidad: '1',
          porcion: '',
          nota: '',
          creadoEn: abierto.creadoEn,
          nombre: previo?.nombre ?? '',
          kcal: previo === null ? '' : texto(previo.valores.kcal),
          proteina: previo === null ? '' : texto(previo.valores.proteinaG),
          carbohidratos: previo === null ? '' : texto(previo.valores.carbohidratosG),
          grasa: previo === null ? '' : texto(previo.valores.grasaG),
          rompeAyuno: previo?.rompeAyuno ?? true,
        }}
        textoGuardar="Guardar"
        onGuardado={(r) => void navegar(rutaDelDia(r.fecha, hoy, r.tiempo), { replace: true })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editar, duplicar y borrar un registro (Req. 18.6)
// ---------------------------------------------------------------------------

export function EditarRegistroPage() {
  const { id = '' } = useParams();
  const [parametros] = useSearchParams();
  const hoy = useFechaHoy();
  const navegar = useNavigate();
  const base = useAlimentosBase();
  const registro = useLiveQuery(async () => (await leerComida(id)) ?? null, [id]);
  const propio = useLiveQuery(
    async () =>
      registro !== undefined && registro !== null && registro.origen.tipo === 'propio'
        ? ((await leerAlimento(registro.origen.refId)) ?? null)
        : null,
    [registro],
  );
  const platillo = useLiveQuery(
    async () =>
      registro !== undefined && registro !== null && registro.origen.tipo === 'platillo'
        ? ((await leerPlatillo(registro.origen.refId)) ?? null)
        : null,
    [registro],
  );
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (registro === undefined || propio === undefined || platillo === undefined) return null;
  if (registro === null) return <NoEncontrado texto="Ese registro ya no existe." volver="/comida" />;

  const volver = rutaDelDia(registro.fecha, hoy, registro.tiempo);

  async function duplicar(r: MealEntry) {
    setOcupado(true);
    setError(null);
    try {
      const copia = await duplicarComida(r.id);
      void navegar(`/comida/registro/${copia.id}?copia=1`, { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  async function borrar(r: MealEntry) {
    setOcupado(true);
    setError(null);
    try {
      await borrarComida(r.id);
      void navegar(volver, { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setOcupado(false);
    }
  }

  const acciones = (
    <div className="space-y-2 pt-2">
      <Button variante="secundario" icono={CopyPlus} disabled={ocupado} onClick={() => void duplicar(registro)}>
        Duplicar
      </Button>
      {confirmarBorrar ? (
        <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
          <p className="text-[13px] text-texto">¿Borrar este registro? No se puede deshacer.</p>
          <div className="grid grid-cols-2 gap-2">
            <Button variante="peligro" disabled={ocupado} onClick={() => void borrar(registro)}>
              Sí, borrar
            </Button>
            <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
          Borrar
        </Button>
      )}
      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}
    </div>
  );

  const inicial: InicialRegistro = {
    id: registro.id,
    fecha: registro.fecha,
    hora: registro.hora,
    tiempo: registro.tiempo,
    cantidad: String(registro.cantidad),
    porcion: registro.porcion ?? GRAMOS,
    nota: registro.nota ?? '',
    creadoEn: registro.creadoEn,
    // Sigue siendo el registro de su comida habitual (0.3.5)
    habitual: registro.habitual ?? null,
  };
  // Al día y tiempo donde quedó el registro, que pueden ser otros si se cambiaron
  const guardado = (r: MealEntry) => void navegar(rutaDelDia(r.fecha, hoy, r.tiempo), { replace: true });

  const encabezado = (
    <PageHeader
      antetitulo={`${NOMBRE_TIEMPO[registro.tiempo]} · ${fechaDelDia(registro.fecha)}`}
      titulo={registro.nombre}
      descripcion={`Editar este registro no cambia el ${registro.origen.tipo === 'platillo' ? 'platillo' : 'alimento'} ni los demás días.`}
      volver={{ a: volver, texto: 'Comida', etiqueta: 'Volver a Comida' }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <EtiquetaEstado estado={estadoDe(registro)} />
        <EtiquetaCalidad calidad={calidadDe(registro)} />
      </div>
    </PageHeader>
  );

  const avisoCopia = parametros.get('copia') === '1' && (
    <div className="mb-5">
      <Banner tono="exito" titulo="Copia creada">
        Quedó en el mismo día, tiempo y hora. Ajusta lo que necesites.
      </Banner>
    </div>
  );

  if (registro.origen.tipo === 'rapido') {
    const texto = (v: number | null) =>
      v === null ? '' : formatearNumero(v, { decimales: 1, fijos: false }).replace(/,/g, '');
    return (
      <div>
        {encabezado}
        {avisoCopia}
        <FormularioRapido
          key={registro.id}
          inicial={{
            ...inicial,
            nombre: registro.nombre,
            kcal: texto(registro.valores.kcal),
            proteina: texto(registro.valores.proteinaG),
            carbohidratos: texto(registro.valores.carbohidratosG),
            grasa: texto(registro.valores.grasaG),
            rompeAyuno: registro.rompeAyuno,
          }}
          textoGuardar="Guardar cambios"
          onGuardado={guardado}
          extra={acciones}
        />
      </div>
    );
  }

  const porciones: readonly Porcion[] =
    registro.origen.tipo === 'base'
      ? ((base.estado === 'lista' ? base.modulo.alimentoBase(registro.origen.refId)?.porciones : undefined) ?? [])
      : registro.origen.tipo === 'platillo'
        ? porcionesDePlatillo(platillo)
        : (propio?.porciones ?? []);
  const calculable = calculableDeRegistro(registro, porciones);

  return (
    <div>
      {encabezado}
      {avisoCopia}
      {calculable === null ? (
        <Banner tono="aviso" titulo="Este registro no tiene gramos ni porción">
          Solo se puede duplicar o borrar.
        </Banner>
      ) : (
        <FormularioRegistro
          key={registro.id}
          fuente={{
            nombre: registro.nombre,
            origen: registro.origen,
            calculable,
            rompeAyuno: registro.rompeAyuno,
            estado: estadoDe(registro),
            esBase: false,
            calidad: calidadDe(registro),
            // La copia guardada, no el platillo de hoy (Req. 18.4)
            ...(registro.origen.tipo === 'platillo'
              ? { nota: 'Valores de lo que registraste: cambiar el platillo después no los cambia.' }
              : {}),
          }}
          inicial={inicial}
          textoGuardar="Guardar cambios"
          onGuardado={guardado}
          extra={acciones}
        />
      )}
      {calculable === null && acciones}
      <p className="mt-6 flex items-start gap-2 text-[12px] leading-relaxed text-texto-suave">
        <Copy className="mt-px size-4" />
        Los valores salen de lo que guardaste al registrarlo, aunque el alimento haya cambiado después.
      </p>
    </div>
  );
}
